import { defineStore } from 'pinia'
import { useLoginStore } from './index'
import { ecp } from '@/utils/rangy'
import { dialogService } from '@/service'
import type {
  HistoryRecord,
  DialogRecord,
  Model,
  StreamOptions,
  EventRecord,
  DeviationDetail,
  Comment
} from '@/service'
import { merge } from 'xe-utils'

export type DialogStore = {
  dialog: DialogRecord['dialog'] // 当前的对话记录
  bilingual: boolean // 是否需要双语显示
  historyRecord: HistoryRecord['page']['list'] // 历史记录
  historyPage: {
    total: number | null // 总条数
    currentPage: number // 当前页码
    pageSize: number // 每页显示条数
  }
  debug: boolean // 是否开启debug模式
  globalModel: Model // 全局配置使用的模型
  dialogStep2: DialogRecord['dialog']
  event_id: EventRecord['event']['event_id']
  ai_review: Pick<DeviationDetail, 'deviation' | 'score' | 'status'>
  qa_review: EventRecord['event']['qa_review']
  step: 1 | 2 | 3 | 4 // 当前偏差对话显示第几步, 1-要素问答 2-报告调整 3-ai初审 4-qa审核
  round: 1 | 2 | 3 | 4 // 当前偏差对话最多能点到第几步
  timeup: number | null // 第二步提交审核延时不让点
}

type IDialog = DialogRecord['dialog']
class Dialog implements IDialog {
  dlg_id = ''
  content = []
  title = ''
  create_time = ''
  update_time = ''
  delete_flag: DeleteFlag = 0
  active_report_id = ''
  advice = ''
  status: DeleteFlag = 0
  constructor(dlg_id?: string) {
    this.dlg_id = dlg_id || ecp.utils.uid()
  }
}

type IContent = IDialog['content'][0]
class Content implements IContent {
  machine = ''
  candidate = []
  machine_eng = ''
  order = 0
  temp_report = ''
  temp_report_eng = ''
  available: IContent['available'] = 0
  isLoading = true
  multi_rounds: IContent['multi_rounds']
  multi_reports: IContent['multi_reports']
  active_report_id = ''
  report = ''
  report_eng = ''
  elementIsShow = true
  constructor(
    public user: string,
    public report_id: string,
    public model: Model,
    account: string
  ) {
    this.multi_rounds = [
      {
        user,
        account,
        machines: [new Machine(model)]
      }
    ]
    this.multi_reports = []
  }
}

type IMachine = IContent['multi_rounds'][0]['machines'][0]
class Machine implements IMachine {
  machine = ''
  candidate = []
  machine_eng = ''
  comment: Comment = 0
  id = ecp.utils.uid()
  constructor(public model: Model) {}
}

const useDialogStore = defineStore('dialogStore', {
  state: (): DialogStore => {
    return {
      dialog: new Dialog(),
      bilingual: false,
      historyRecord: [],
      historyPage: {
        total: null,
        currentPage: 0,
        pageSize: 20
      },
      debug: false,
      globalModel: 'q2_72b',
      dialogStep2: new Dialog(),
      event_id: '',
      ai_review: {
        deviation: [],
        score: [],
        status: 0
      },
      qa_review: null,
      step: 1,
      round: 1,
      timeup: null
    }
  },
  actions: {
    /**
     * @description 新启对话
     */
    resetDialog() {
      this.dialog = new Dialog()
      this.dialogStep2 = new Dialog()
      this.ai_review = {
        deviation: [],
        score: [],
        status: 0
      }
      this.event_id = ''
      this.qa_review = null
      this.step = 1
      this.round = 1
      this.bilingual = false
    },
    resetTimer() {
      this.timeup = 10
      let timer = setInterval(() => {
        if (this.timeup) {
          this.timeup--
          if (this.timeup === 0) {
            this.timeup = null
            clearTimeout(timer)
          }
        }
      }, 1000)
    },
    /**
     * @description 添加对话内容
     * @param id report_id, 传则会重新回答对应的问题
     */
    async addDialog(text: string, id?: string, model?: Model) {
      const loginStore = useLoginStore()
      const account = loginStore.userInfo?.account || ''
      const lang = (localStorage.getItem('lang') ||
        'zh-cn') as NotNullOrUndefined<StreamOptions['configurable']>['lang']
      let report_id = id || ecp.utils.uid()
      let i: number | undefined
      let originModel = model
      if (!id) {
        // 插入新一轮的对话
        let lastContent = this.dialog.content.at(-1)
        let content = new Content(
          text,
          report_id,
          lastContent?.model || 'q2_72b',
          account
        )
        this.dialog.content.push(content)
        model = content.model
      } else {
        let index = this.dialog.content.findLastIndex(
          (item) => item.report_id === id
        )
        let content = this.dialog.content[index]
        if (~index) {
          i = index + 1
          if (model) {
            // 切换模型重新回答，记录到多轮对话中
            let round = content.multi_rounds.find(
              (round) => round.user === text
            )!
            round.machines.push(new Machine(model))
            content.model = model
          } else {
            model = content.model || 'q2_72b'
            if (text !== content['user']) {
              // 修改问题重新回答，记录到多轮对话中
              content.multi_rounds.push({
                user: text,
                account,
                machines: [new Machine(model)]
              })
            } else {
              // 相同的问题重新回答
              let round = content.multi_rounds.find(
                (round) => round.user === text
              )!
              round.machines.push(new Machine(model))
              content.page = round.machines.length
            }
          }
          merge(this.dialog.content[index], {
            machine: '',
            machine_eng: '',
            isLoading: true,
            user: text
          })
        }
      }
      let configurable: StreamOptions['configurable'] = {
        lang
      }
      originModel && (configurable['model'] = originModel)
      const stream = await dialogService.getAnswer(
        this.dialog.content.slice(0, i),
        configurable
      )
      console.log(stream)
      if (!stream.content) {
        // 返回的数据格式不稳定..
        stream.content = (stream as any).question || ''
      }
      let index = this.dialog.content.findLastIndex(
        (item) => item.report_id === report_id
      )
      if (~index) {
        let candidates = [stream.content, ...stream.candidate].map((item) => ({
          zh_cn: item,
          en_us: ''
        }))
        let content = {
          ...this.dialog.content[index],
          machine: stream.content,
          candidate: candidates
        }
        this.dialog.content.splice(index, 1, content)
        content.isLoading = false
        let translateRes = await Promise.all(
          candidates.map((i) =>
            dialogService.getEnglishTranslate(i['zh_cn'], {
              configurable: {
                lang,
                model: model!
              }
            })
          )
        )
        let engArray = translateRes.map((i) => i?.content || '')
        let round = content.multi_rounds.find((round) => round.user === text)
        if (round) {
          let lastMachine = round.machines.at(-1)!
          lastMachine.machine = stream.content
          lastMachine.candidate = candidates.map((item, index) => {
            return {
              zh_cn: item['zh_cn'],
              en_us: engArray[index] || ''
            }
          })
          lastMachine.machine_eng = lastMachine.candidate[0]['en_us']
          dialogService.updateContentByMachine(content, lastMachine, text)
          originModel && (content.page = round.machines.length)
        }
        this.dialog.content = [...this.dialog.content]
        let res = await this._saveDialogTemp(this.dialog.dlg_id)
        if (res && dialogService.isSuccess(res)) {
          this.event_id = res.data.eventInfo.event_id
        }
      }
      if (stream.action === 'finish') {
        this.getReport()
      }
    },
    /**
     * @description 生成偏差报告
     */
    async getReport() {
      let content = this.dialog.content.at(-1)
      if (!content) return
      let lang = (localStorage.getItem('lang') ||
        'zh-cn') as NotNullOrUndefined<StreamOptions['configurable']>['lang']
      content.isGenReport = true
      content.isGenning = true
      let id = ecp.utils.uid()
      content.report = ''
      content.report_eng = ''
      let configurable: StreamOptions['configurable'] = { lang }
      this.debug && (configurable.model = this.globalModel)
      let finalReport = await dialogService.getReport(this.dialog.content, {
        onMessage(msg) {
          if (content.isGenReport) content.isGenReport = false
          content.report += msg
        },
        configurable
      })
      content.report =
        finalReport || content.report.replace(/```text(.*)```/s, '$1').trim()
      if (content.isGenReport) content.isGenReport = false
      let newReport = {
        id,
        report: content.report,
        report_eng: content.report_eng
      }
      if (this.bilingual) {
        await dialogService.getEnglishTranslate(content.report, {
          stream: true,
          onMessage(msg) {
            content.report_eng += msg
          },
          configurable: {
            lang,
            model: this.globalModel
          }
        })
        newReport.report_eng = content.report_eng
      } else {
        this._saveDialogTemp(this.dialog.dlg_id, async () => {
          let eng = await dialogService.getEnglishTranslate(content.report)
          let ctx = this.dialog.content.find(
            (item) => item.report_id === content.report_id
          )
          if (ctx) {
            let report = ctx.multi_reports.find(
              (item) => item.id === newReport.id
            )
            if (report) {
              report.report_eng = eng?.content || ''
              if (ctx.active_report_id === report.id)
                ctx.report_eng = report.report_eng
              return true
            }
          }
          return false
        })
      }
      content.multi_reports.push(newReport)
      content.active_report_id = id
      let res = await this._saveDialogTemp(this.dialog.dlg_id)
      if (
        res &&
        dialogService.isSuccess(res) &&
        !this.dialogStep2.content.length
      ) {
        // 将 report_dlg_id 保存到后端
        this.dialogStep2 = new Dialog(this.dialogStep2.dlg_id)
        this.round = 2
        await this._saveReportAdjustment(this.event_id)
      }
      let ctx = this.dialog.content.find(
        (item) => item.report_id === content.report_id
      )
      if (ctx) {
        ctx.isGenning = false
        this.dialog = {
          ...this.dialog
        }
      }
    },
    /**
     * @description 重新生成偏差报告
     */
    async refresh() {
      return this.getReport()
    },
    /**
     * @description 获取历史记录
     * @param page_number 第几页，从1开始
     */
    async getHistoryRecord() {
      if (
        this.historyPage.total &&
        this.historyPage.total <=
          this.historyPage.pageSize * this.historyPage.currentPage
      )
        return
      let res = await dialogService.getHistoryRecord({
        page_number: this.historyPage.currentPage + 1,
        page_size: this.historyPage.pageSize
      })
      if (dialogService.isSuccess(res)) {
        let { list, total, pageNo, pageSize } = res.data.page
        this.historyRecord.push(...list)
        this.historyPage = {
          total,
          currentPage: pageNo,
          pageSize
        }
      }
    },
    /**
     * @description 重置历史记录
     */
    resetHistoryRecord() {
      this.historyRecord = []
      this.historyPage = {
        total: null,
        currentPage: 0,
        pageSize: 20
      }
    },
    /**
     * @description 跳转到某条历史记录
     */
    async getDialogRecord(event_id?: EventRecord['event']['event_id']) {
      if (!event_id) {
        if (this.dialog.content.length) this.resetDialog()
        this.onUpdateStep(1)
        return
      }
      let res = await dialogService.getEventRecord(event_id)
      if (dialogService.isSuccess(res)) {
        this.dialog = res.data.event.qa_dialog
        if (!res.data.event.report_dialog) {
          let step2 = new Dialog()
          res.data.event.report_dialog = step2
          await dialogService.addReportAdjustment(res.data.event.event_id, {
            dlg_id: step2.dlg_id,
            content: step2.content
          })
        }
        this.dialogStep2 = res.data.event.report_dialog
        this.event_id = res.data.event.event_id
        if (res.data.event.ai_review) {
          this.ai_review = res.data.event.ai_review
        }
        this.qa_review = res.data.event.qa_review
        this.step = res.data.event.round || 1
        this.round = res.data.event.round || 1
        return this.qa_review
      } else {
        throw Error('获取对话记录失败')
      }
    },
    /**
     * @description 要素检查
     */
    async getElementCheck() {
      let lang = (localStorage.getItem('lang') ||
        'zh-cn') as NotNullOrUndefined<StreamOptions['configurable']>['lang']
      let lastContent = this.dialog.content.at(-1)
      if (lastContent?.elements) return lastContent.elements
      let res = await dialogService.getElementCheck(this.dialog.content, {
        configurable: {
          lang
        }
      })
      let check_result = res.data.check_result
        .filter((item) => item.reason)
        .map((i) => i.reason)
      if (lastContent) {
        lastContent.elements = check_result
      }
      return check_result
    },
    /**
     * @description 从一二两步获取最新生成的偏差报告
     */
    getLastReportContent(
      dialogStep2Content: DialogStore['dialogStep2']['content'],
      dialogContent?: DialogStore['dialog']['content']
    ) {
      dialogContent = dialogContent || this.dialog.content
      let content = dialogStep2Content.findLast((content) => content.report)
      if (!content)
        content = dialogContent.findLast((content) => content.report)
      return content
    },
    /**
     * @description 添加对话内容
     * @param id report_id, 传则会重新回答对应的问题
     */
    async addDialogStep2(text: string, id?: string, model?: Model) {
      const loginStore = useLoginStore()
      const account = loginStore.userInfo?.account || ''
      const lang = (localStorage.getItem('lang') ||
        'zh-cn') as NotNullOrUndefined<StreamOptions['configurable']>['lang']
      let report_id = id || ecp.utils.uid()
      let i: number | undefined
      let originModel = model
      if (!id) {
        // 插入新一轮的对话
        let lastContent = this.dialogStep2.content.at(-1)
        let content = new Content(
          text,
          report_id,
          lastContent?.model || 'q2_72b',
          account
        )
        this.dialogStep2.content.push(content)
        model = content.model
      } else {
        let index = this.dialogStep2.content.findLastIndex(
          (item) => item.report_id === id
        )
        let content = this.dialogStep2.content[index]
        if (~index) {
          i = index
          if (model) {
            // 切换模型重新回答，记录到多轮对话中
            let round = content.multi_rounds.find(
              (round) => round.user === text
            )!
            round.machines.splice(0, 1, new Machine(model))
            content.model = model
          } else {
            model = content.model || 'q2_72b'
            if (text !== content['user']) {
              // 修改问题重新回答，记录到多轮对话中
              content.multi_rounds.splice(0, 1, {
                user: text,
                account,
                machines: [new Machine(model)]
              })
            } else {
              // 相同的问题重新回答
              // let round = content.multi_rounds.find(
              //   (round) => round.user === text
              // )!
            }
          }
          merge(this.dialogStep2.content[index], {
            machine: '',
            machine_eng: '',
            isLoading: true,
            user: text
          })
        }
      }
      let configurable: StreamOptions['configurable'] = {
        lang
      }
      originModel && (configurable['model'] = originModel)
      let report_content = this.getLastReportContent(
        this.dialogStep2.content.slice(0, i)
      )?.report
      if (!report_content) return ecp.utils.message.error('未找到最后一篇报告')
      const stream = await dialogService.getReportAdjust(
        this.dialogStep2.content.slice(0, i),
        report_content,
        text,
        configurable
      )
      console.log(stream)
      let index = this.dialogStep2.content.findLastIndex(
        (item) => item.report_id === report_id
      )
      if (~index) {
        if (stream.data.action === 'question') {
          let content = this.dialogStep2.content[index]
          let round = content.multi_rounds.find((round) => round.user === text)
          if (round) {
            let lastMachine = round.machines.at(-1)!
            lastMachine.machine = stream.data.question_output.content
            const fn = () => {
              return dialogService.getEnglishTranslate(lastMachine.machine, {
                configurable: {
                  lang,
                  model: this.globalModel
                }
              })
            }
            if (this.bilingual) {
              // 开了双语，同步翻译英文
              let eng = await fn()
              lastMachine.machine_eng = eng?.content || ''
            } else {
              // 没开双语，异步翻译英文后再调一次保存接口
              this._saveReportAdjustment(this.event_id, async () => {
                let eng = await fn()
                let ctx = this.dialogStep2.content.find(
                  (item) => item.report_id === content.report_id
                )
                if (ctx) {
                  let { machine } = dialogService.findMachineById(
                    lastMachine.id,
                    ctx
                  )
                  if (machine) {
                    machine.machine_eng = eng?.content || ''
                    if (ctx.active_id === machine.id)
                      ctx.machine_eng = machine.machine_eng
                    return true
                  }
                }
                return false
              })
            }
            lastMachine.original_content = stream.raw.content
            dialogService.updateContentByMachine(content, lastMachine, text)
            originModel && (content.page = round.machines.length)
          }
          content.isLoading = false
        } else {
          let content = this.dialogStep2.content[index]
          let round = content.multi_rounds.find((round) => round.user === text)
          if (round) {
            let lastMachine = round.machines.at(-1)!
            lastMachine.original_content = stream.raw.content
            lastMachine.machine = ''
            lastMachine.machine_eng = ''
            dialogService.updateContentByMachine(content, lastMachine, text)
            originModel && (content.page = round.machines.length)
            let id = ecp.utils.uid()
            content.report =
              stream.data.action === 'edit'
                ? stream.data.edit_output.reduce((res, item) => {
                    return res.replaceAll(item.origin, item.update)
                  }, report_content)
                : stream.data.rewrite_output.new_report
            let newReport = {
              id,
              report: content.report,
              report_eng: content.report_eng
            }
            if (this.bilingual) {
              // 开了双语显示，则同步等待英文翻译后再保存到后台
              await dialogService.getEnglishTranslate(content.report, {
                stream: true,
                onMessage(msg) {
                  content.report_eng += msg
                },
                configurable: {
                  lang,
                  model: this.globalModel
                }
              })
              newReport.report_eng = content.report_eng
            } else {
              // 未开双语，则异步获取英文翻译，再另调一次保存接口到后台
              this._saveReportAdjustment(this.event_id, async () => {
                let eng = await dialogService.getEnglishTranslate(
                  content.report,
                  {
                    configurable: {
                      lang,
                      model: this.globalModel
                    }
                  }
                )
                let ctx = this.dialogStep2.content.find(
                  (item) => item.report_id === content.report_id
                )
                if (ctx) {
                  let report = ctx.multi_reports.find(
                    (item) => item.id === newReport.id
                  )
                  if (report) {
                    report.report_eng = eng?.content || ''
                    if (ctx.active_report_id === report.id)
                      ctx.report_eng = report.report_eng
                    return true
                  }
                }
                return false
              })
            }
            content.multi_reports.splice(0, 1, newReport)
            content.active_report_id = id
          }
          content.isLoading = false
        }
        this.dialogStep2.content = [...this.dialogStep2.content]
        this._saveReportAdjustment(this.event_id)
      }
    },
    async onUpdateStep(step: DialogStore['step']) {
      this.step = step
    },
    /**
     * @description 将报告调整记录保存到后台
     * @param fn 调保存接口前的回调函数, 主要用于异步获取英文翻译后保存
     */
    async _saveReportAdjustment(
      event_id: DialogStore['event_id'],
      fn?: () => Promise<boolean>
    ) {
      let bool = true
      if (fn) {
        bool = await fn()
      }
      if (this.event_id !== event_id || !bool) return
      return dialogService.addReportAdjustment(this.event_id, {
        dlg_id: this.dialogStep2.dlg_id,
        content: this.dialogStep2.content
      })
    },
    /**
     * @description 将要素问答记录保存到后台
     * @param fn 调保存接口前的回调函数, 主要用于异步获取英文翻译后保存, 返回值表式是否还需要调保存接口
     */
    async _saveDialogTemp(
      dlg_id: DialogStore['dialog']['dlg_id'],
      fn?: () => Promise<boolean>
    ) {
      let bool = true
      if (fn) {
        bool = await fn()
      }
      if (this.dialog.dlg_id !== dlg_id || !bool) return
      return dialogService.addDialogTemp({
        dlg_id: this.dialog.dlg_id,
        content: this.dialog.content
      })
    }
  },
  getters: {
    /**
     * @description 是否还有更多历史记录
     */
    hasMoreRecord(): boolean {
      if (this.historyPage.total === null) return true
      return (
        this.historyPage.total >
        this.historyPage.currentPage * this.historyPage.pageSize
      )
    },
    /**
     * @returns {Boolean} 报告是否正在审核
     */
    isAudit(): boolean {
      if (!this.qa_review) return false
      return this.qa_review[0].status === 0
    }
  }
})

export default useDialogStore
